import React from "react";
import { Link } from "react-router-dom";
import StorefrontLayout from "../components/storefront/Layout";
import { useSeo } from "../lib/seo";
import { HelpCircle, ArrowRight } from "lucide-react";

// FAQ items. Answers are grounded in the site's Return, Shipping and Refund policies.
export const FAQS = [
  {
    q: "How do I know a part fits my vehicle?",
    a: "Match the part to your exact year, make, model and trim, and confirm it carries the same features your original glass has. If anything is uncertain, contact us before ordering and our team will verify fitment for you.",
  },
  {
    q: "Can my VIN help identify the correct part?",
    a: "Yes. Your VIN reflects how your vehicle was built and helps confirm your configuration. You may also find markings on the back of your original mirror glass. Share these details with us and we'll cross-check the correct listing.",
  },
  {
    q: "What's the difference between driver-side and passenger-side mirror glass?",
    a: "Side mirror glass is side-specific and not interchangeable. The left/driver-side and right/passenger-side pieces are shaped and curved differently, so be sure to order the correct side. Many customers order a pair when both mirrors are aging.",
  },
  {
    q: "How do I know if I need heated or feature-equipped mirror glass?",
    a: "Check the features on your existing mirror, such as heating, a blind-spot indicator, or auto-dimming. Your replacement should carry the same features. If your current mirror is heated, choose heated glass so the function keeps working.",
  },
  {
    q: "What do OEM and aftermarket mean?",
    a: "OEM refers to a part made to the original manufacturer's specification, while aftermarket parts are made by other companies. We offer OEM-style and quality aftermarket options. Either way, the part must match your vehicle and its features. See our OEM vs. aftermarket guide for a full explanation.",
  },
  {
    q: "Does the vehicle name on a listing mean it's a manufacturer-branded part?",
    a: "No. A vehicle's name on a listing describes compatibility — what the part fits. It does not, by itself, mean the item is a vehicle-manufacturer branded product. Lecis Auto Glass does not manufacture vehicle-brand OEM products; each listing states what the item is.",
  },
  {
    q: "Does Lecis Auto Glass provide installation?",
    a: "No. We supply replacement side mirrors, windshields, backglass and sunroof glass, but we do not perform installation. We recommend having glass installed by a qualified professional.",
  },
  {
    q: "Do you ship nationwide?",
    a: "We ship across the contiguous United States from Fair Oaks, California. Shipping to Alaska, Hawaii, U.S. territories, P.O. boxes, or international destinations may be limited or unavailable. If we can't ship to your address, we'll contact you and issue a full refund.",
  },
  {
    q: "How are windshields and backglass shipped?",
    a: "Windshields, backglass and other large or fragile items are classified as oversized and ship via freight (LTL) carriers to protect the glass. Freight deliveries are curbside, may require a signature, and can take additional transit time compared to standard ground service.",
  },
  {
    q: "How long will my order take?",
    a: "In-stock orders are typically processed within 1–3 business days. Standard ground transit is usually 2–7 business days depending on your destination, and delivery estimates begin once the order ships. Oversized freight items may take additional time.",
  },
  {
    q: "What if the product I need isn't listed on the site?",
    a: "It may still be available even if it isn't listed yet. Contact us with your vehicle details and a description of the glass, and we'll help confirm fitment and look into sourcing options.",
  },
  {
    q: "What is your return policy?",
    a: "Eligible items may be returned within 30 days of delivery. Items must be unused, uninstalled and in original, resalable condition with all original packaging and components, and proof of purchase is required. Contact us for a Return Authorization before shipping anything back.",
  },
  {
    q: "Who pays for return shipping?",
    a: "For approved returns handled according to our Return Policy, Lecis Auto Glass covers the cost of return shipping. Once your Return Authorization is issued, we provide a prepaid return label or reimburse the agreed return shipping cost.",
  },
  {
    q: "What if my product arrives damaged in transit?",
    a: "Inspect every shipment on delivery. For freight deliveries, note any visible damage on the carrier's receipt before signing and photograph the packaging and product. Report shipping damage to support@lecisautoglass.com within 48 hours of delivery with your order number and photos, and we'll arrange a replacement or refund for verified transit damage.",
  },
  {
    q: "What if I order the wrong part?",
    a: "If you ordered the wrong item, our standard Return Policy applies: eligible items can be returned within 30 days if unused, uninstalled and in resalable condition. Contact us for a Return Authorization. To avoid this, we're glad to confirm fitment before you buy.",
  },
];

export default function Faq() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  useSeo({
    title: "Frequently Asked Questions — Lecis Auto Glass",
    description:
      "Answers to common questions about ordering side mirrors, windshields, backglass and sunroof glass from Lecis Auto Glass — fitment, shipping, returns and support.",
    canonicalPath: "/faq",
    jsonLd:
      FAQS.length > 0
        ? {
            faq: {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              url: `${origin}/faq`,
              mainEntity: FAQS.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          }
        : undefined,
  });

  return (
    <StorefrontLayout>
      <div className="max-w-3xl mx-auto px-6 lg:px-12 py-16 md:py-24" data-testid="faq-page">
        <div className="text-[10px] uppercase tracking-[0.4em] text-red-500 font-bold mb-6">
          FAQs
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-bold mb-6">
          Frequently asked questions.
        </h1>
        <p className="text-zinc-400 text-base md:text-lg leading-relaxed mb-12">
          Answers about fitment, ordering, shipping and returns for exotic and luxury auto glass.
        </p>

        {FAQS.length === 0 ? (
          <div className="border border-white/10 rounded-md p-10 text-center" data-testid="faq-empty">
            <HelpCircle size={26} className="text-red-500 mx-auto mb-4" />
            <div className="font-display text-xl mb-2">FAQs coming soon</div>
            <p className="text-sm text-zinc-400 max-w-md mx-auto leading-relaxed">
              We're putting together answers to the questions we hear most. Have a question now?
              Our team is happy to help.
            </p>
            <Link
              to="/contact"
              data-testid="faq-contact-cta"
              className="inline-flex items-center gap-2 mt-8 bg-red-600 hover:bg-red-700 text-white px-5 h-11 text-sm rounded-sm transition"
            >
              Contact &amp; Fitment Help <ArrowRight size={15} />
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-white/10 border-y border-white/10" data-testid="faq-list">
            {FAQS.map((f, i) => (
              <div key={i} className="py-6" data-testid={`faq-item-${i}`}>
                <h2 className="font-display text-lg mb-2">{f.q}</h2>
                <p className="text-sm text-zinc-400 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </StorefrontLayout>
  );
}
